"use client";

import Link from "next/link";
import { useFormState } from "react-dom";
import { usePathname } from "next/navigation";
import { track } from "@vercel/analytics";
import { trackEvent } from "@/lib/analytics";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/forms/SubmitButton";
import { subscribeFromModal } from "@/app/email/newsletter/actions";

const topicDisplayNames: Record<string, string> = {
  newsletter: "Newsletter",
  "warteliste-mitgliedschaft": "Mitgliedschaft",
  "warteliste-zugspitze-reintal-2026": "Zugspitze Reintal 2026",
  "warteliste-zugspitze-gatterl-2026": "Zugspitze Gatterl 2026",
  "warteliste-xalps-2027": "xAlps 2027",
};

type SignupModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buttonLabel?: string;
  signupTopic: string;
};

const SignupModal = ({
  open,
  onOpenChange,
  buttonLabel,
  signupTopic,
}: SignupModalProps) => {
  const [state, formAction] = useFormState(subscribeFromModal, {});
  const pathname = usePathname();
  const subline = topicDisplayNames[signupTopic] || signupTopic;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-sky-50 rounded-3xl border-none max-w-md p-6">
        {state?.success ? (
          <div className="flex flex-col gap-2 items-center text-center py-4">
            <DialogTitle className="sr-only">Bestätigung</DialogTitle>
            <h2 className="text-headline-md text-black">
              Du hast eine E-Mail erhalten
            </h2>
            <p className="text-copy text-black">
              Vielen Dank für Deine Anmeldung. Prüfe bitte Deinen Posteingang,
              um diese zu bestätigen.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 items-center text-center">
            <DialogTitle className="sr-only">Warteliste</DialogTitle>
            <div className="flex flex-col gap-1">
              <p className="text-subline text-black">{subline}</p>
              <h2 className="text-headline-md text-black">
                Trage Dich jetzt auf die Warteliste ein
              </h2>
            </div>

            <form action={formAction} className="w-full flex flex-col gap-1">
              <input type="hidden" name="topic" value={signupTopic} />
              <input type="hidden" name="source" value={pathname} />
              <Input
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Deine E-Mail"
                className="w-full !h-[46px] bg-transparent rounded-full px-5 border border-white !text-lg text-black placeholder:!text-lg placeholder:text-black"
                required
              />
              {state?.error && (
                <p className="text-copy-sm text-red-500">{state.error}</p>
              )}
              <SubmitButton
                variant="sun"
                size="sm"
                className="w-full h-[46px]"
                onClick={() => {
                  track("Newsletter submit");
                  // Siehe EmailForm: nur der Klick, nicht die Anmeldung.
                  trackEvent("newsletter_submit_attempted", { topic: signupTopic });
                }}
              >
                {buttonLabel || "Anmelden"}
              </SubmitButton>

              <p className="text-copy-sm text-center mt-1 mx-auto max-w-[80%]">
                Mit Absenden des Formulars stimme ich den{" "}
                <Link href="/datenschutz" className="underline">
                  Datenschutzbestimmungen
                </Link>{" "}
                zu
              </p>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { SignupModal };
